import React from 'react';
import { Badge, Button, Card, FormField, Input } from '../ui';
import { InstagramChallengeInput } from '../InstagramChallengeInput';
import { InstagramRegionField } from '../InstagramRegionField';
import { InstagramCooldownNotice, isInCooldown } from '../InstagramCooldownNotice';
import { findCountry } from '../../constants/countries';
import type { Workspace } from '../../types';

type TestResult = {
  valid: boolean;
  username?: string;
  error?: string;
  authStatus?: string;
};

type InstagramConnectCardProps = {
  workspace: Workspace;
  setShowInstagram2FAHelp: React.Dispatch<React.SetStateAction<boolean>>;
  igCountry: string;
  handleIgCountryChange: (_code: string) => void;
  igUsername: string;
  handleIgUsernameChange: (_e: React.ChangeEvent<HTMLInputElement>) => void;
  hasSession: boolean;
  showIgPassword: boolean;
  igPassword: string;
  handleIgPasswordChange: (_e: React.ChangeEvent<HTMLInputElement>) => void;
  setShowIgPassword: React.Dispatch<React.SetStateAction<boolean>>;
  deletingSession: boolean;
  handleDeleteSession: () => void;
  challengePending: boolean;
  challengeType: 'email' | 'sms' | 'totp';
  maskedContact: string;
  submittingChallenge: boolean;
  challengeError: string | undefined;
  handleSubmitChallenge: (_code: string) => void;
  handleCancelChallenge: () => void;
  handleResendChallenge: () => Promise<{ sent: boolean; error?: string }>;
  testingIg: boolean;
  savingSession: boolean;
  igTestResult: TestResult | null;
  handleTestInstagrapi: () => void;
  // Official Instagram Business Login (Graph OAuth). Admin-gated + beta:
  // the flow is not fully working yet, so the button is hidden for non-admins.
  isAdmin: boolean;
  connectingOAuth: boolean;
  disconnectingOAuth: boolean;
  handleConnectInstagramOAuth: () => void;
  handleDisconnectInstagramOAuth: () => void;
};

export function InstagramConnectCard({
  workspace,
  setShowInstagram2FAHelp,
  igCountry,
  handleIgCountryChange,
  igUsername,
  handleIgUsernameChange,
  hasSession,
  showIgPassword,
  igPassword,
  handleIgPasswordChange,
  setShowIgPassword,
  deletingSession,
  handleDeleteSession,
  challengePending,
  challengeType,
  maskedContact,
  submittingChallenge,
  challengeError,
  handleSubmitChallenge,
  handleCancelChallenge,
  handleResendChallenge,
  testingIg,
  savingSession,
  igTestResult,
  handleTestInstagrapi,
  isAdmin,
  connectingOAuth,
  disconnectingOAuth,
  handleConnectInstagramOAuth,
  handleDisconnectInstagramOAuth,
}: InstagramConnectCardProps) {
  const cooling = isInCooldown(workspace.instagram_cooldown_until);

  return (
    <Card elevation="raised">
      <Card.Header>
        <Card.Title subtitle="Connect your Instagram account so Conistra can publish for you.">
          <span className="settings-section-icon">📸</span>
          <span className="settings-title-with-help">
            <span>Instagram Account</span>
            <button
              type="button"
              className="settings-help-icon"
              aria-label="Open Instagram setup help"
              onClick={() => setShowInstagram2FAHelp(true)}
            >
              ?
            </button>
          </span>
        </Card.Title>
      </Card.Header>
      <Card.Body className="settings-card-body">
        {cooling && (
          <InstagramCooldownNotice until={workspace.instagram_cooldown_until} />
        )}
        {isAdmin && (
          <div className="settings-ig-oauth">
            <div className="settings-ig-oauth__head">
              <span className="settings-ig-oauth__title">Connect with Instagram (official)</span>
              <Badge variant="neutral">Admin · Beta</Badge>
            </div>
            <p className="settings-ig-oauth__hint">
              One-click login via Instagram&apos;s official Business Login — no password needed.
              This flow is still experimental and only visible to admins while we finish it.
            </p>
            {workspace.has_instagram_token ? (
              <div className="settings-ig-session-status">
                <Badge variant="approved" dot>
                  Connected via Instagram Login
                  {workspace.instagram_handle ? ` as @${workspace.instagram_handle}` : ''}
                </Badge>
                <Button
                  variant="danger"
                  size="sm"
                  loading={disconnectingOAuth}
                  onClick={handleDisconnectInstagramOAuth}
                >
                  Disconnect
                </Button>
              </div>
            ) : (
              <Button
                variant="primary"
                size="sm"
                loading={connectingOAuth}
                onClick={handleConnectInstagramOAuth}
              >
                Connect with Instagram
              </Button>
            )}
          </div>
        )}

        <div className="settings-ig-wizard">
          <div className="settings-ig-step">
            <div className="settings-ig-step__title">1. Your Instagram profile</div>
            <FormField
              label="Username"
              htmlFor="ig-instagrapi-user"
              hint="Without the @"
            >
              <Input
                id="ig-instagrapi-user"
                value={igUsername}
                onChange={handleIgUsernameChange}
                placeholder="e.g. my_gym_account"
              />
            </FormField>

            {!hasSession && (
              <FormField
                label="Password"
                htmlFor="ig-instagrapi-pass"
                hint="Used once, never stored."
              >
                <div className="settings-password-row">
                  <Input
                    id="ig-instagrapi-pass"
                    type={showIgPassword ? 'text' : 'password'}
                    value={igPassword}
                    onChange={handleIgPasswordChange}
                    placeholder="Enter password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowIgPassword((prev) => !prev)}
                    aria-label={showIgPassword ? 'Hide Instagram password' : 'Show Instagram password'}
                  >
                    {showIgPassword ? 'Hide' : 'Show'}
                  </Button>
                </div>
              </FormField>
            )}
          </div>

          <div className="settings-ig-step">
            <div className="settings-ig-step__title">2. Two-factor authentication</div>
            <div className="settings-ig-tip">
              <span className="settings-ig-tip__icon" aria-hidden="true">🔐</span>
              <p>
                Use an <strong>authenticator app</strong> — Google Authenticator,
                Authy, 1Password. Much more reliable than SMS.{' '}
                <button
                  type="button"
                  className="settings-ig-tip__link"
                  onClick={() => setShowInstagram2FAHelp(true)}
                >
                  Why?
                </button>
              </p>
            </div>
          </div>

          <div className="settings-ig-step">
            <div className="settings-ig-step__title">3. Publishing region</div>
            <div className="settings-ig-step__hint">
              Match your own country unless this account logs in from elsewhere.
            </div>
            <InstagramRegionField
              value={igCountry}
              onChange={handleIgCountryChange}
              idPrefix="settings-ig-region"
              disabled={hasSession}
            />
            {hasSession && (
              <p className="settings-hint-text">Disconnect below to change region.</p>
            )}
          </div>

          <div className="settings-ig-step">
            <div className="settings-ig-step__title">4. Connect</div>

            {hasSession ? (
              <div className="settings-ig-session-status">
                <Badge variant="approved" dot>
                  @{igUsername || 'your account'} · {findCountry(igCountry)?.flag ?? ''}{' '}
                  {findCountry(igCountry)?.label ?? igCountry}
                </Badge>
                <Button
                  variant="danger"
                  size="sm"
                  loading={deletingSession}
                  onClick={handleDeleteSession}
                  disabled={cooling}
                  title={
                    cooling
                      ? 'Publishing is paused. Deleting the session now would force a new login, which makes a soft block worse.'
                      : undefined
                  }
                >
                  Disconnect
                </Button>
              </div>
            ) : challengePending ? (
              <InstagramChallengeInput
                challengeType={challengeType}
                maskedContact={maskedContact}
                loading={submittingChallenge}
                error={challengeError}
                onSubmit={handleSubmitChallenge}
                onCancel={handleCancelChallenge}
                onResend={challengeType === 'totp' ? undefined : handleResendChallenge}
              />
            ) : (
              <>
                <div className="settings-ig-actions">
                  <div className="settings-ig-status">
                    {(testingIg || savingSession) && (
                      <Badge variant="neutral" dot>
                        Connecting…
                      </Badge>
                    )}
                    {!testingIg && !savingSession && igTestResult?.valid && (
                      <Badge variant="approved" dot>
                        Connected as @{igTestResult.username}
                      </Badge>
                    )}
                  </div>
                  <div className="settings-ig-btn-group">
                    <Button
                      variant="primary"
                      loading={testingIg || savingSession}
                      onClick={handleTestInstagrapi}
                      disabled={
                        !igUsername || !igPassword || testingIg || savingSession || cooling
                      }
                    >
                      Connect Instagram
                    </Button>
                  </div>
                </div>
                <p className="settings-hint-text">If it fails, wait a minute before retrying.</p>
              </>
            )}

            {/* A cooldown outranks the attention flag: the breaker sets both,
                but "reconnect" is the wrong instruction during a soft block. */}
            {!cooling && workspace.instagram_needs_attention && (
              <div className="settings-needs-attention">
                <strong>Action needed:</strong> Reconnect your account below.
              </div>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
